import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import EFilterButton from '@/components/everridge/EFilterButton';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { FilterHelper } from '@/utils/filterHelper';
import { cn } from '@/lib/utils';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import SuperAdminBookingsPageFilter from '@/features/superAdmin/features/data/bookings/components/SuperAdminBookingsPageFilter';
import { useSuperAdminBookingsPageColumns } from '@/features/superAdmin/features/data/bookings/components/SuperAdminBookingsPageColumns';
import { useSuperAdminBookingsFilter } from '@/features/superAdmin/features/data/bookings/hooks/useSuperAdminBookingsFilter';
import { useSuperAdminBookingsListApi } from '@/features/superAdmin/features/data/bookings/hooks/useSuperAdminBookingsListApi';
import { SuperAdminBookingListItemDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

const SuperAdminBookingsListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const columns = useSuperAdminBookingsPageColumns();
    const { filter, setFilter, resetFilter } = useSuperAdminBookingsFilter();
    const [page, setPage] = useState(() => Math.floor(filter.skip / filter.take));
    const [showFilter, setShowFilter] = useState(false);
    const { list, isLoading } = useSuperAdminBookingsListApi({ suspendFetching: showFilter });

    useEffect(() => {
        const totalCount = list?.totalCount ?? 0;
        if ((totalCount === 0 && filter.skip > 0) || (totalCount > 0 && filter.skip >= totalCount)) {
            setFilter({ skip: 0 });
            setPage(0);
        }
    }, [list?.totalCount, filter.skip, setFilter]);

    useEffect(() => {
        setPage(Math.floor(filter.skip / filter.take));
    }, [filter.skip, filter.take]);

    if (userRole !== Roles.superAdmin) return null;

    const activeFiltersCount = FilterHelper.countFilterValues(filter, [
        'take',
        'skip',
        'orderBy',
        'orderDirection',
        'tenantName',
        'hotelName',
        'ownerName',
        'apartmentName',
    ]);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setFilter({ skip: filter.take * newPage });
    };

    const handleRowClick = (booking: SuperAdminBookingListItemDto) => {
        navigate(`/superAdmin/data/bookings/${booking.id}`, { state: { booking } });
    };

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader title={t('superAdmin.data.bookings.title')}>
                <Button variant="outline" className="gap-2" onClick={() => navigate('/superAdmin/data')}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('superAdmin.data.backToHub')}
                </Button>
                <EFilterButton
                    name={t('elements.components.filter.title')}
                    filtersQuantity={activeFiltersCount}
                    onClick={() => setShowFilter(true)}
                />
            </EPageHeader>

            <ETableWithApiPagination
                columns={columns}
                data={list}
                page={page}
                setPage={handlePageChange}
                paginationStep={filter.take}
                isLoadingPage={isLoading}
                onRowClick={handleRowClick}
            />

            {showFilter && (
                <SuperAdminBookingsPageFilter
                    onClose={() => setShowFilter(false)}
                    isMobile={isMobile}
                    filter={filter}
                    setFilter={setFilter}
                    resetFilter={resetFilter}
                />
            )}
        </section>
    );
};

export default SuperAdminBookingsListPage;
